import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { ModalOverlayBoundary } from './overlay-environment';

import { cn } from '@/lib/utils';

import { SHEET_APPEARANCE, SHEET_SIDE } from './types/sheet-variants-enums';





const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const foundationSheetOverlayClasses =
  'fixed inset-0 z-overlay bg-overlay-scrim data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none';

interface SheetOverlayProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> {
  appearance?: SHEET_APPEARANCE;
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  SheetOverlayProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <SheetPrimitive.Overlay
      className={cn(foundationSheetOverlayClasses, className)}
      data-slot="sheet-overlay"
      {...props}
      ref={ref}
    />
  );
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const foundationSheetContentBaseClasses =
  'fixed z-portal gap-4 border-border-default bg-surface-base p-6 text-content-primary shadow-overlay outline-none transition-[transform] duration-deliberate ease-standard data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-standard data-[state=open]:duration-deliberate motion-reduce:transition-none motion-reduce:animate-none';

const foundationSheetSideClasses: Record<SHEET_SIDE, string> = {
  [SHEET_SIDE.LEFT]:
    'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
  [SHEET_SIDE.RIGHT]:
    'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
};

const foundationSheetCloseClasses =
  'absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-control text-content-secondary transition-colors duration-standard ease-standard hover:bg-neutral-interaction-hover hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 disabled:pointer-events-none motion-reduce:transition-none pointer-coarse:min-h-11 pointer-coarse:min-w-11';

type SheetContentProps = Omit<
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  'children'
> & {
  children?: React.ReactNode;
  appearance?: SHEET_APPEARANCE;
  side?: SHEET_SIDE;
};

function resolveFoundationSide(side: string | undefined): SHEET_SIDE {
  return side === SHEET_SIDE.LEFT || side === 'left' ? SHEET_SIDE.LEFT : SHEET_SIDE.RIGHT;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ appearance: _appearance, side = SHEET_SIDE.RIGHT, className, children, ...props }, ref) => {
  const foundationSide = resolveFoundationSide(side);

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        data-slot="sheet-content"
        data-side={foundationSide}
        className={cn(
          foundationSheetContentBaseClasses,
          foundationSheetSideClasses[foundationSide],
          className
        )}
        {...props}
      >
        <ModalOverlayBoundary>{children}</ModalOverlayBoundary>
        <SheetPrimitive.Close
          data-slot="sheet-close"
          aria-label="Close"
          className={foundationSheetCloseClasses}
        >
          <X className="size-3.5" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-header"
    className={cn('flex flex-col space-y-2 text-center sm:text-left', className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-footer"
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

interface SheetTitleProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title> {
  appearance?: SHEET_APPEARANCE;
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  SheetTitleProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <SheetPrimitive.Title
      ref={ref}
      data-slot="sheet-title"
      className={cn('type-dialog-title font-semibold text-content-primary', className)}
      {...props}
    />
  );
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;

interface SheetDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description> {
  appearance?: SHEET_APPEARANCE;
}

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  SheetDescriptionProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <SheetPrimitive.Description
      ref={ref}
      data-slot="sheet-description"
      className={cn('text-sm text-content-secondary', className)}
      {...props}
    />
  );
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger };

export type { SheetContentProps, SheetOverlayProps };
